import { noop } from 'lodash';
import { useState } from 'react';

import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { useIntl } from 'react-intl';

import Box from '../Box/Box';
import Checkbox from '../Checkbox/Checkbox';
import FlexLayout from '../FlexLayout/FlexLayout';
import Folder from '../Folder/Folder';
import Icon from '../Icon/Icon';
import Radio from '../RadioGroup/Radio';
import Text from '../Text/Text';

const iconColor = 'gray-500';
const iconSize = 'm';
const space = 4;

function FolderNode({
  ariaProps,
  folder,
  isBranch,
  isExpanded,
  isIndeterminate,
  isMulti,
  isSelected,
  level,
  style,
  onExpand,
  onFetchSubfolders,
  onSelect,
}) {
  const intl = useIntl();
  const [isFetching, setIsFetching] = useState(false);
  const [hasSubfolders, setHasSubfolders] = useState(
    !!onFetchSubfolders || isBranch,
  );

  const { id, disabled, tooltip, expandable = true } = folder;

  const getIndentWidth = (theme) => {
    const spaceMargin = space * 4;
    const iconWidth = theme.sizes[`icon-${iconSize}`];
    return `calc(${level - 1} * (${spaceMargin}px + ${iconWidth}))`;
  };

  const bg = isSelected ? 'blue-100' : undefined;
  const option = { label: '', value: id };
  const indentStyle = { ml: getIndentWidth };
  const chevronVisible = expandable && hasSubfolders;

  const input = isMulti ? (
    <Checkbox value={isIndeterminate ? null : isSelected} onChange={noop} />
  ) : (
    <Radio selected={isSelected} option={option} onChange={noop} />
  );

  const handleExpand = async (event) => {
    if (onFetchSubfolders && !isBranch) {
      try {
        setIsFetching(true);
        const subfolders = await onFetchSubfolders(folder);
        setHasSubfolders(subfolders.length > 0);
      } catch {
        // swallow error: consumers to handle
      } finally {
        setIsFetching(false);
      }
    }
    event.stopPropagation();
    onExpand(!isExpanded);
  };

  return (
    <Box tabIndex={-1} {...ariaProps} bg={bg} sx={style} onClick={onSelect}>
      <FlexLayout alignItems="center" py={3} space={space} sx={indentStyle}>
        <Icon
          color={iconColor}
          icon={isExpanded ? 'chevronDown' : 'chevronRight'}
          size={iconSize}
          sx={{ visibility: chevronVisible ? 'visible' : 'hidden' }}
          tabIndex={-1}
          title={
            isExpanded
              ? intl.formatMessage({
                  id: 'FolderNode.hideSubfolders',
                  defaultMessage: 'Hide subfolders',
                  description: 'Tooltip for collapse folder tree icon',
                })
              : intl.formatMessage({
                  id: 'FolderNode.showSubfolders',
                  defaultMessage: 'Show subfolders',
                  description: 'Tooltip for expand folder tree icon',
                })
          }
          onClick={handleExpand}
        />
        <Tooltip tooltip={tooltip}>
          <FlexLayout
            alignItems="center"
            disabled={disabled || isFetching}
            flex="auto"
            space={space}
          >
            {input}
            <Folder folder={folder} isConcise={true} isLoading={isFetching} />
            {!hasSubfolders && (
              <Text color="gray-500" variant="2xs-dense">
                {intl.formatMessage({
                  id: 'FolderNode.noSubfolders',
                  defaultMessage: 'No subfolders',
                  description: 'Text displayed when folder has no subfolders',
                })}
              </Text>
            )}
          </FlexLayout>
        </Tooltip>
      </FlexLayout>
    </Box>
  );
}

export default FolderNode;
